/**
 * Global provider for site page visibility. Each entry in `pages` is an
 * object shape `{show_in_header, show_in_footer, page_active}` — three
 * independent flags per page.
 *
 * Helpers:
 *   • useHeaderVisible(key)  — should the nav link render?
 *   • useFooterVisible(key)  — should the footer link render?
 *   • useRouteActive(key)    — should the direct URL resolve to content?
 *
 * All helpers fail-open (return true) when the visibility map is still
 * loading OR the key is missing — so an API hiccup never blanks out the
 * whole site.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const Ctx = createContext({ pages: {}, groups: {}, loading: true, refresh: () => {} });

function _entry(pages, key) {
  const v = pages[key];
  if (v === undefined) return null;
  // Legacy bool support — server still normalises, but keep client safe.
  if (typeof v === "boolean") return { show_in_header: v, show_in_footer: v, page_active: v };
  return v;
}

export function SitePageVisibilityProvider({ children }) {
  const [state, setState] = useState({ pages: {}, groups: {}, loading: true });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/public/site-pages/visibility");
      setState({ pages: data.data.pages || {}, groups: data.data.groups || {}, loading: false });
    } catch {
      setState({ pages: {}, groups: {}, loading: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return <Ctx.Provider value={{ ...state, refresh }}>{children}</Ctx.Provider>;
}

export function useSitePageVisibility() {
  return useContext(Ctx);
}

/** Nav link — visible only when the page is BOTH active and header-listed. */
export function useHeaderVisible(key) {
  const { pages, loading } = useContext(Ctx);
  if (loading) return true;
  const e = _entry(pages, key);
  if (!e) return true;
  return !!(e.show_in_header && e.page_active);
}

/** Footer link — visible only when the page is BOTH active and footer-listed. */
export function useFooterVisible(key) {
  const { pages, loading } = useContext(Ctx);
  if (loading) return true;
  const e = _entry(pages, key);
  if (!e) return true;
  return !!(e.show_in_footer && e.page_active);
}

/** Direct URL — the route resolves to real content only when the page is active. */
export function useRouteActive(key) {
  const { pages, loading } = useContext(Ctx);
  if (loading) return true;
  const e = _entry(pages, key);
  if (!e) return true;
  return !!e.page_active;
}
